import PageHeader from "components/Page/PageHeader";
import useRelatorioPrestacaoContas from "core/features/relatorios/prestacao-contas/hooks/useListaRelatoriosPrestacaoContas";
import useRelatorioPrestacaoContasColumns from "./../../hooks/useRelatorioPrestacaoContasColumns";
import { useLoading } from "components/GlobalLoading";
import {
  ListaRelatorioPrestacaoContasResponse,
  RelatorioPrestacaoContasForm,
} from "features/relatorios/prestacao-contas/typings";
import NewTable from "components/NewTable";
import HeaderTable from "components/Page/HeaderTable";
import useFiltro from "infra/helpers/useFiltro";
import moment from "moment";
import { useCallback, useState } from "react";
import { makeBlobOrAttachmentUrlList } from "../../util/makeBlobOrAttachmentUrlList";
import { useCurrentTenant } from "core/features/auth/tenant.store";
import { showError, showConfirm } from "infra/helpers/alerta";
import ModalConfiguracaoRelatorioPrestacaoContas from "./../ModalConfiguracaoRelatorio";
import useDeleteRelatorioPrestacaoContas from "core/features/relatorios/prestacao-contas/hooks/useDeleteRelatorioPrestacaoContas";
import useDuplicarRelatorioPrestacaoContas from "core/features/relatorios/prestacao-contas/hooks/useDuplicarRelatorioPrestacaoContas";
import { showSuccess } from "infra/helpers/alerta";
import { downloadLocalBlob } from "infra/helpers/download";
import { mergePdfsFromBlobOrUrlList } from "infra/helpers/mergePdfsFromBlobOrUrl";
import { pdfAddPageNumbers } from "infra/helpers/pdfAddPageNumbers";
import { logError } from "infra/packages/@sentry/logError";
import { Flex } from "@chakra-ui/react";
import SelectMonthYear from "ui/components/Input/SelectMonthYear";
import ModalDuplicarTenants from "../ModalDuplicarTenants/index";

function ListagemRelatoriosPrestacaoContas() {
  const { tenant } = useCurrentTenant();
  const { filtro, onChangeFiltro } = useFiltro();
  const { data, isLoading, isFetching, refetch } =
    useRelatorioPrestacaoContas(filtro);
  const { mutate: deleteRelatorio, isLoading: isLoadingDelete } =
    useDeleteRelatorioPrestacaoContas();
  const { mutate: duplicarRelatorio, isLoading: isLoadingDuplicar } =
    useDuplicarRelatorioPrestacaoContas();

  const columns = useRelatorioPrestacaoContasColumns();
  const [loadingPdfMessage, setLoadingPdfMessage] = useState<
    string | undefined
  >();

  const [isOpenModalConfiguracao, setIsOpenModalConfiguracao] = useState(false);

  const [modalDuplicarTenantsRelatorioId, setModalDuplicarTenantsRelatorioId] =
    useState<string | undefined>();

  const [modalRelatorioConfiguracao, setModalRelatorioConfiguracao] = useState<
    { id: string; mode: "download" | "save" } | undefined
  >();

  const _onExportPdfFromForm = useCallback(
    async (
      data: RelatorioPrestacaoContasForm,
      incluirNumeroPaginas?: boolean
    ) => {
      try {
        if (data?.items?.length) {
          const _items = data?.items.map((item) => ({
            ...item,
            mes: filtro.mes,
          }));

          const blobOrUrlList = await makeBlobOrAttachmentUrlList(
            _items,
            tenant.imagemUrl,
            (i, total) =>
              setLoadingPdfMessage(
                `Gerando Relatórios...\n Progresso: ${i + 1} / ${total}`
              )
          );

          const blob = await mergePdfsFromBlobOrUrlList(
            blobOrUrlList,
            (i, total) =>
              setLoadingPdfMessage(
                `Unindo relatórios...\nProgresso: ${i + 1} de ${total}`
              )
          );

          const arrayBuffer = await blob.arrayBuffer();

          let downloadable: Blob | Uint8Array = blob;

          if (incluirNumeroPaginas) {
            try {
              downloadable = await pdfAddPageNumbers(arrayBuffer);
            } catch (err) {
              console.log("Error adding page numbers", err);
              downloadable = blob;
            }
          }

          setLoadingPdfMessage("Gerando pdf...");

          downloadLocalBlob(
            downloadable,
            `Relatório de Prestação de Contas - ${moment().format(
              "DD_MM_YYYY"
            )}.pdf`,
            "application/pdf"
          );
        } else if (!!data?.items) {
          showError(
            "Ocorreu um erro ao exportar o PDF. Verifique a configuração do relatório."
          );
        }
      } catch (err) {
        console.log("Erro ao gerar pdf", err);
        logError(err);
        showError(err as any);
      } finally {
        setLoadingPdfMessage(undefined);
      }
    },
    [filtro.mes, tenant.imagemUrl]
  );

  useLoading(
    isLoading ||
      isFetching ||
      isLoadingDelete ||
      !!loadingPdfMessage ||
      isLoadingDuplicar,
    loadingPdfMessage
  );

  return (
    <>
      <PageHeader
        title="Relatórios de prestação de contas"
        onClick={() => setIsOpenModalConfiguracao(true)}
        midComponent={
          <Flex>
            <SelectMonthYear
              bg="white"
              value={filtro.mes ? moment(filtro.mes, "YYYY-MM") : undefined}
              placeholder="Filtrar por mês"
              onChange={(date) =>
                onChangeFiltro({ mes: date?.format("YYYY-MM") })
              }
              allowClear
            />
          </Flex>
        }
        titleButton="Criar novo relatório"
      />

      <NewTable<ListaRelatorioPrestacaoContasResponse>
        title={() => (
          <HeaderTable
            value={filtro.search ?? ""}
            onSearch={(search) => onChangeFiltro("search", search)}
          />
        )}
        loading={isFetching}
        columns={columns}
        dataSource={data?.items}
        actions={[
          {
            title: "Baixar PDF",
            onClick: (row) => {
              setModalRelatorioConfiguracao({ id: row.id, mode: "download" });
            },
          },
          {
            title: "Editar",
            onClick: (row) => {
              setModalRelatorioConfiguracao({ id: row.id, mode: "save" });
            },
          },
          {
            title: "Duplicar",
            onClick: async (row) => {
              const { isConfirmed } = await showConfirm(
                "Deseja realmente duplicar esse relatório?"
              );

              if (isConfirmed) {
                duplicarRelatorio(row.id, {
                  onSuccess: ({ data }) => {
                    showSuccess(
                      `Relatório duplicado com o nome ${data.nome}`
                    ).then(() => refetch());
                  },
                });
              }
            },
          },
          {
            title: "Duplicar para outros condomínios",
            onClick: async (row) => {
              setModalDuplicarTenantsRelatorioId(row.id);
            },
          },
          {
            title: "Excluir",
            onClick: async (row) => {
              const { isConfirmed } = await showConfirm(
                "Deseja realmente excluir esse relatório?"
              );

              if (isConfirmed) {
                deleteRelatorio(row.id, {
                  onSuccess: () => {
                    showSuccess("Relatório deletado com sucesso").then(() =>
                      refetch()
                    );
                  },
                });
              }
            },
          },
        ]}
        customPagination={{
          currentPage: data?.meta.currentPage ?? 0,
          totalItems: data?.meta.totalItems ?? 0,
          itemsPerPage: data?.meta.itemsPerPage ?? 0,
          onChangePage: (page) => onChangeFiltro("page", page),
        }}
      />

      {!!modalRelatorioConfiguracao?.id || isOpenModalConfiguracao ? (
        <ModalConfiguracaoRelatorioPrestacaoContas
          relatorioPrestacaoContasId={modalRelatorioConfiguracao?.id}
          isOpen={!!modalRelatorioConfiguracao?.id || isOpenModalConfiguracao}
          onClose={() => {
            setIsOpenModalConfiguracao(false);
            setModalRelatorioConfiguracao(undefined);
          }}
          refetch={refetch}
          month={filtro?.mes}
          mode={modalRelatorioConfiguracao?.mode}
          onDownload={_onExportPdfFromForm}
        />
      ) : null}

      {!!modalDuplicarTenantsRelatorioId ? (
        <ModalDuplicarTenants
          refetch={refetch}
          onClose={() => {
            setModalDuplicarTenantsRelatorioId(undefined);
          }}
          visible={!!modalDuplicarTenantsRelatorioId}
          relatorioPrestacaoContasId={modalDuplicarTenantsRelatorioId}
        />
      ) : null}
    </>
  );
}

export default ListagemRelatoriosPrestacaoContas;
